import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCar, FaGasPump, FaCog } from 'react-icons/fa';
import { vehicleAPI, resolveMediaUrl } from '../services/api';
import './VehiclesPage.css';

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Our Vehicles | Kariyapperuma Car Sales';
    return () => { document.title = 'Kariyapperuma Car Sales'; };
  }, []);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const response = await vehicleAPI.getAll();
      if (response.data.success) {
        setVehicles(response.data.data);
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price >= 1000000) {
      const millions = parseFloat((price / 1000000).toFixed(2));
      return `Rs. ${millions} Million`;
    }
    return `Rs. ${Number(price).toLocaleString('en-LK')}`;
  };

  const formatMileage = (mileage) => {
    if (typeof mileage !== 'number') {
      return 'Mileage on request';
    }

    return `${mileage.toLocaleString()} km`;
  };

  const getStatusDisplay = (status) => {
    if (status === 'available') {
      return { className: 'in-stock', badge: 'IN STOCK', label: 'Available now' };
    }
    if (status === 'reserved') {
      return { className: 'reserved', badge: 'RESERVED', label: 'Reserved' };
    }
    return { className: 'sold-out', badge: 'SOLD OUT', label: 'Sold out' };
  };

  const SkeletonCard = () => (
    <motion.div
      className="vehicle-card skeleton-card"
      initial={{ opacity: 0.6 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, repeat: Infinity }}
    >
      <div className="vehicle-card-image">
        <div className="skeleton-image"></div>
      </div>
      <div className="vehicle-info">
        <div className="skeleton-title"></div>
        <div className="skeleton-year"></div>
        <div className="skeleton-specs">
          <div className="skeleton-spec"></div>
          <div className="skeleton-spec"></div>
          <div className="skeleton-spec"></div>
        </div>
        <div className="skeleton-features">
          <div className="skeleton-feature"></div>
          <div className="skeleton-feature"></div>
        </div>
        <div className="skeleton-pricing">
          <div className="skeleton-price"></div>
        </div>
        <div className="skeleton-button"></div>
      </div>
    </motion.div>
  );

  const VehicleCard = ({ vehicle, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const statusDisplay = getStatusDisplay(vehicle.status);
    const primaryImage = vehicle.images && vehicle.images.length > 0 ? vehicle.images[0] : null;

    return (
      <motion.div
        className="vehicle-card"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        whileHover={{ y: -8 }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05, duration: 0.4, hover: { duration: 0.3 } }}
      >
        <div className="vehicle-card-image">
          <div className="vehicle-image-wrapper">
            {primaryImage ? (
              <img
                src={resolveMediaUrl(primaryImage)}
                alt={`${vehicle.make} ${vehicle.model}`}
                className="vehicle-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const wrapper = e.target.parentElement;
                  const placeholder = wrapper.parentElement.querySelector('.vehicle-placeholder');
                  if (placeholder) {
                    placeholder.style.display = 'flex';
                  }
                }}
              />
            ) : null}
          </div>

          <div
            className="vehicle-placeholder"
            style={{
              display: primaryImage ? 'none' : 'flex',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          >
            <FaCar />
          </div>

          <motion.span
            className={`vehicle-status ${statusDisplay.className}`}
            animate={isHovered ? { scale: 1.06 } : { scale: 1 }}
          >
            {statusDisplay.badge}
          </motion.span>

          {vehicle.badge && (
            <span className="vehicle-special-badge">{vehicle.badge}</span>
          )}
        </div>

        <div className="vehicle-info">
          <div className="vehicle-topline">
            <span className="vehicle-category">{vehicle.bodyType || 'Vehicle'}</span>
          </div>

          <h3 className="vehicle-name">{vehicle.make} {vehicle.model}</h3>

          <div className="vehicle-meta-row">
            <span className="vehicle-meta-pill">{vehicle.year}</span>
            <span className="vehicle-meta-pill">{formatMileage(vehicle.mileage)}</span>
          </div>

          <div className="vehicle-specs">
            <motion.span className="spec-item" whileHover={{ scale: 1.05 }}>
              <FaGasPump className="spec-icon" />
              {vehicle.fuelType}
            </motion.span>
            <motion.span className="spec-item" whileHover={{ scale: 1.05 }}>
              <FaCog className="spec-icon" />
              {vehicle.transmission}
            </motion.span>
            <motion.span className="spec-item" whileHover={{ scale: 1.05 }}>
              <FaCar className="spec-icon" />
              {vehicle.bodyType}
            </motion.span>
          </div>

          <div className="vehicle-features-preview">
            {vehicle.features && vehicle.features.length > 0 ? (
              <>
                {vehicle.features.slice(0, 2).map((feature, idx) => (
                  <span key={idx} className="feature-tag">
                    {feature}
                  </span>
                ))}
                {vehicle.features.length > 2 && (
                  <span className="feature-more">
                    +{vehicle.features.length - 2} more features
                  </span>
                )}
              </>
            ) : (
              <span className="feature-more">Contact for full specification</span>
            )}
          </div>

          <div className="vehicle-footer-row">
            <div className="vehicle-pricing">
              <div className="vehicle-price-main">{formatPrice(vehicle.price)}</div>
              <div className="vehicle-price-sublabel">Landed price estimate</div>
            </div>

            <div className="vehicle-actions">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to={`/vehicles/${vehicle.id}`}
                  className="btn btn-primary btn-sm btn-block"
                >
                  View Details
                </Link>
              </motion.div>
            </div>
          </div>

          <div className="vehicle-status-line">
            <span className={`vehicle-status-dot ${statusDisplay.className}`}></span>
            <span>{statusDisplay.label}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="vehicles-page">
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="page-title">Our Vehicle Collection</h1>
            <p className="page-subtitle">
              Browse our selection of high-quality unregistered imports
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container vehicles-container">
        <div className="results-info">
          <h3>
            {loading ? 'Loading...' : `${vehicles.length} Vehicle${vehicles.length !== 1 ? 's' : ''} Available`}
          </h3>
        </div>

        {loading ? (
          <motion.div
            className="vehicles-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {[...Array(6)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </motion.div>
        ) : vehicles.length === 0 ? (
          <div className="no-results">
            <FaCar className="no-results-icon" />
            <h3>No vehicles found</h3>
            <p>Please check back soon for new inventory</p>
          </div>
        ) : (
          <motion.div
            className="vehicles-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {vehicles.map((vehicle, index) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                index={index}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default VehiclesPage;
